import React, { memo, useEffect, useMemo, useState } from 'react';
import { 
    Box, 
    Checkbox, 
    InputAdornment, 
    List, 
    ListItem, 
    ListItemButton, 
    ListItemIcon, 
    ListItemText, 
    ListSubheader, 
    TextField 
} from '@mui/material';
import { debounce, isEmpty, lowerCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';
import SearchIcon from '@mui/icons-material/Search'; // Assuming you have MUI icons
import { TAnalyticParams } from '../../shared/types/analyticParameters.type';
import ListSkeleton from '../exams/loader/ListSkeleton';

type AvailableParamsProps = {
    searchEnabled?: boolean;
    useVirtualisedList?: boolean;
    groupedParams: Partial<Record<string, TAnalyticParams[]>>;
    loading: boolean;
    selectedParams: TAnalyticParams[];
    allParams: TAnalyticParams[];
    onParamCheck: (item: TAnalyticParams, checked: boolean) => void;
};

function AvailableParams({ 
    useVirtualisedList = false, 
    groupedParams, 
    loading, 
    selectedParams, 
    allParams, 
    onParamCheck, 
    searchEnabled = true 
}: AvailableParamsProps) {
    const { t } = useTranslation();
    const [filteredGroups, setFilteredGroups] = useState<Partial<Record<string, TAnalyticParams[]>>>({});

    useEffect(() => {
        setFilteredGroups(groupedParams);
    }, [groupedParams]);

    // OPTIMIZATION: Use a Set for O(1) lookups instead of O(n) array includes.
    // Fixed dependency array to rely on the actual array, not just its length.
    const selectedNamesSet = useMemo(() => {
        if (isEmpty(selectedParams)) return new Set<string>();
        return new Set(selectedParams.map(e => lowerCase(e.name)));
    }, [selectedParams]);

    // OPTIMIZATION: Memoize the debounced function so it isn't recreated on every render.
    const handleSearch = useMemo(() => debounce((value: string) => {
        if (!value) {
            setFilteredGroups(groupedParams);
            return;
        }
        
        const newData: Partial<Record<string, TAnalyticParams[]>> = {};
        const regex = new RegExp(value, 'i'); 

        Object.entries(groupedParams).forEach(([key, items]) => {
            const filteredItems = items?.filter(v => regex.test(v.name_translated));
            if (!isEmpty(filteredItems)) {
                newData[key] = filteredItems;
            }
        });

        setFilteredGroups(newData);
    }, 300), [groupedParams]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            handleSearch.cancel();
        };
    }, [handleSearch]);

    const renderItem = (item: TAnalyticParams) => {
        const isChecked = selectedNamesSet.has(lowerCase(item.name));

        return (
            <ListItem disableGutters disablePadding key={item.name}>
                <ListItemButton 
                    onClick={() => onParamCheck(item, !isChecked)}
                    sx={{ 
                        borderRadius: 1.5, 
                        mx: 1, 
                        mb: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': { backgroundColor: 'action.hover' }
                    }}
                >
                    <ListItemText 
                        primary={item.name_translated} 
                        secondary={
                            <Box 
                                component="span" 
                                dangerouslySetInnerHTML={{ __html: item.details_translated }} 
                                sx={{ 
                                    display: 'block', 
                                    typography: 'body2', 
                                    color: 'text.secondary',
                                    mt: 0.5
                                }} 
                            />
                        }
                        primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                        <Checkbox 
                            edge="end"
                            checked={isChecked}
                            tabIndex={-1}
                            disableRipple
                        />
                    </ListItemIcon>
                </ListItemButton>
            </ListItem>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {searchEnabled && (
                <TextField 
                    onChange={e => handleSearch(e.target.value)} 
                    placeholder={t('search')} 
                    fullWidth 
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ 
                        mb: 2,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 } 
                    }} 
                />
            )}
            
            <Box 
                sx={{ 
                    flex: 1, 
                    overflowY: 'auto', 
                    maxHeight: "350px",
                    // Modern scrollbar styling
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { 
                        background: 'rgba(0,0,0,0.1)', 
                        borderRadius: '10px',
                        '&:hover': { background: 'rgba(0,0,0,0.2)' }
                    }
                }} 
            >
                {useVirtualisedList ? (
                    <Virtuoso
                        style={{ height: "350px" }}
                        totalCount={allParams.length}
                        data={allParams}
                        itemContent={(_, item) => renderItem(item)}
                    />
                ) : (
                    <List disablePadding sx={{ position: 'relative' }} className='parentList'>
                        {loading ? (
                            <ListSkeleton height={80} length={8} />
                        ) : (
                            Object.entries(filteredGroups).map(([groupKey, items]) => (
                                <React.Fragment key={groupKey}>
                                    <ListSubheader 
                                        className={groupKey} 
                                        sx={{ 
                                            bgcolor: 'rgba(255, 255, 255, 0.8)', // Frosted glass effect
                                            backdropFilter: 'blur(8px)',
                                            fontWeight: 600,
                                            lineHeight: '40px',
                                            color: 'text.primary'
                                        }}
                                    >
                                        {t(`dataParameters:groups:${groupKey}`)}
                                    </ListSubheader>
                                    
                                    {items?.map(item => renderItem(item))}
                                </React.Fragment>
                            ))
                        )}
                    </List>
                )}
            </Box>
        </Box>
    );
}

export default memo(AvailableParams);